import { useEffect, useMemo, useState } from 'react'
import { sunAltitudeDeg, dayFactor } from '@/lib/sunTint'

export type TintMode = 'day' | 'night' | 'auto'

/**
 * Dev/verify override so the tint is testable without waiting for the clock:
 *   ?tint=day   → force full day (blue)
 *   ?tint=night → force full night (almost-black purple)
 *   ?tint=auto  → live, sun-driven (the shipped default; also any other/absent value)
 */
export function readTintMode(): TintMode {
  if (typeof window === 'undefined') return 'auto'
  const v = new URLSearchParams(window.location.search).get('tint')
  return v === 'day' || v === 'night' ? v : 'auto'
}

/**
 * Live day factor in [0, 1] for lat/lon, honoring the ?tint override. In `auto` it
 * recomputes off a 30s tick (matching the home refresh cadence); the consumer eases the
 * visual crossfade in CSS. With no location it falls back to night (0).
 */
export function useDayFactor(lat: number | null, lon: number | null): number {
  const mode = useMemo(readTintMode, [])
  const [now, setNow] = useState(() => new Date())

  useEffect(() => {
    if (mode !== 'auto') return // a forced override never ticks
    const id = setInterval(() => setNow(new Date()), 30_000)
    return () => clearInterval(id)
  }, [mode])

  if (mode === 'day') return 1
  if (mode === 'night') return 0
  if (lat == null || lon == null) return 0
  return dayFactor(sunAltitudeDeg(now, lat, lon))
}
